// SYSTEM-MAP-1 — reading the map, in three states.
//
// THIS WAVE BUILDS THE MAP; THE NEXT ONE READS IT. What is here is the one
// read the map needs to be usable at all — load an account's nodes and edges —
// and nothing else. There is deliberately no search, no traversal and no
// impact analysis: those are the exploration tools, and they are the next
// wave's, not this one's.

import { supabase } from './supabase'
import { interpretMap, type SystemMapRead } from './systemMap'
import { TAB_PREFIX, type TabRow } from './builtTabs'

export interface MapNode {
  address: string
  nodeType: string
  displayName: string
  description: string
  /** A LABEL. Never ownership — see systemMap.ts. May not resolve. */
  homeSection: string | null
}

export interface MapEdge {
  from: string
  to: string
  relationship: string
}

export interface SystemMap {
  nodes: MapNode[]
  edges: MapEdge[]
}

/**
 * Load one account's map.
 *
 * TWO READS, AND A FAILURE IN EITHER FAILS THE WHOLE THING. A map with its
 * nodes but not its edges is not a partial success, it is a map that says
 * nothing is related to anything — which is indistinguishable from a business
 * that has configured nothing, and would be read as exactly that.
 */
export async function loadSystemMap(subAccountId: string): Promise<SystemMapRead<SystemMap>> {
  // Boundary cast: the map tables are not in the generated types, because this
  // migration is print-only and nothing has regenerated them. Same seam
  // customFieldsRepo.ts uses for the same reason, and it disappears on its own
  // the moment the migration is applied and the types are regenerated.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any

  const nodesRes = await sb
    .from('system_map_nodes')
    .select('address, node_type, display_name, description, home_section')
    .eq('sub_account_id', subAccountId)
    .is('removed_at', null)
    .order('address')

  // Returned BEFORE the edges are read. Asking for edges after the nodes read
  // failed would either mask the first failure or report the second one, and
  // the merchant needs to be told about the first.
  if (nodesRes.error) {
    return interpretMap<SystemMap>({ data: null, error: nodesRes.error })
  }

  const edgesRes = await sb
    .from('system_map_edges')
    .select('from_address, to_address, relationship')
    .eq('sub_account_id', subAccountId)
    .order('from_address')

  if (edgesRes.error) {
    return interpretMap<SystemMap>({ data: null, error: edgesRes.error })
  }

  const nodes: MapNode[] = (nodesRes.data ?? []).map((r: Record<string, unknown>) => ({
    address: String(r.address),
    nodeType: String(r.node_type),
    displayName: String(r.display_name),
    description: String(r.description),
    homeSection: r.home_section === null ? null : String(r.home_section),
  }))

  const edges: MapEdge[] = (edgesRes.data ?? []).map((r: Record<string, unknown>) => ({
    from: String(r.from_address),
    to: String(r.to_address),
    relationship: String(r.relationship),
  }))

  return interpretMap<SystemMap>({ data: { nodes, edges }, error: null })
}

/**
 * THE-TAB-IS-IN-THE-MENU-1 — THE BUILT TABS, AND NOTHING ELSE OF THE MAP.
 *
 * `loadSystemMap` reads every node and every edge, 3,868 nodes across
 * production on 2026-09-16, which is not a read a menu makes on every page.
 * This asks for the tab namespace only, live rows only, oldest first and
 * bounded. IT THROWS rather than answering empty, so a failed read can never be
 * drawn as "nothing built", the rule `listArtifacts` already keeps.
 */
/**
 * THE-DIVIDER-HOLDS-ONLY-WHAT-IS-RULED-1 — THE SECTIONS AND CONNECTIONS THIS
 * ACCOUNT HAS BUILT, from the same table and under the same member read a built
 * tab uses. Addresses only: what the menu asks is whether a thing of that kind
 * exists, never what it is called.
 */
export async function listBuiltMenuAddresses(subAccountId: string): Promise<string[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const { data, error } = await sb
    .from('system_map_nodes')
    .select('address')
    .eq('sub_account_id', subAccountId)
    .in('node_type', ['section', 'connection'])
    .is('removed_at', null)
    .limit(200)
  if (error) throw new Error(error.message ?? 'the built sections could not be read')
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => String(r.address))
}

export async function listBuiltTabRows(subAccountId: string): Promise<TabRow[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const { data, error } = await sb
    .from('system_map_nodes')
    .select('address, display_name, created_at, removed_at')
    .eq('sub_account_id', subAccountId)
    .like('address', `${TAB_PREFIX}.%`)
    .is('removed_at', null)
    .order('created_at', { ascending: true })
    .limit(200)
  if (error) throw new Error(error.message ?? 'the built tabs could not be read')
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    address: String(r.address),
    display_name: String(r.display_name),
    created_at: String(r.created_at),
    removed_at: r.removed_at == null ? null : String(r.removed_at),
  }))
}
